import React, { useEffect, useState } from "react";
import { GetStaticProps } from "next";
import Image from "next/image";
import Link from "next/link";
import cn from "classnames";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import classes from "@/styles/register/Register.module.scss";
import { SeoHead } from "@/components/common/Seo";
import Alert from "@/components/common/Alert";
import { When } from "@/components/common/When";
import Validation from "@/components/common/Validation/Validation";
import { OrLine } from "@/components/common/OrLine/OrLine";
import { GoogleButton } from "@/components/common/GoogleButton/GoogleButton";
import useRegister from "@/hooks/user/register/useRegister";
import useGoogleSignIn from "@/hooks/user/register/useGoogleSignIn";
import { useIANATimezoneDatabase, useRegisterTrack, useUserState } from "@/states/user.state";
import { isValidEmail, returnErrorAsString } from "@/helper/helper";
import { cookiePersistStorage } from "@/helper/common.helper";
import { AiOutlineEyeInvisible, AiOutlineEye } from "react-icons/ai";
import { BiLoaderAlt } from "react-icons/bi";
import { useError } from "@/states/common.state";
import { logoAltImg } from "@/constant";
import { LuAlertTriangle } from "react-icons/lu";
import { AzureButton } from "@/components/common/AzureButton";
import { signIn } from "next-auth/react";
import { useTranslations } from "next-intl";
import { sendGTMEvent } from "@next/third-parties/google";
import { gtm } from "@/gtm/events";
import useMicrosoftSignIn from "@/hooks/user/register/useMicrosoftSignIn";
const Register = () => {
  const router = useRouter();
  const {
    query
  } = router;
  const {
    email
  } = query;
  const {
    trackProperty,
    setRegisterTrack
  } = useRegisterTrack();
  const {
    timeZoneDb
  } = useIANATimezoneDatabase();
  const {
    typedEmail
  } = useUserState();
  const {
    isGoogleLoading,
    onSignIn: onSignInGoogle
  } = useGoogleSignIn(() => sendGTMEvent({
    event: gtm.register.google_register_success
  }));
  const {
    onSignIn: onSignInAzure
  } = useMicrosoftSignIn(() => sendGTMEvent({
    event: gtm.register.azure_register_success
  }));
  const t = useTranslations("LoginAndSignup");
  const isError = query?.isError as unknown as boolean;
  const access_token = cookiePersistStorage.getItem("access_token");
  const handleRegisterTrack = () => {
    if (!trackProperty) setRegisterTrack("/register");
  };
  const {
    error,
    setError,
    setDeleteError
  } = useError();
  const [isConfirmEye, setIsConfirmEye] = useState<boolean>(false);
  const hooksForm = useForm({
    defaultValues: {
      email,
      password: ""
    }
  });
  const {
    setMutate,
    isLoading,
    isSuccess
  } = useRegister();
  const onSubmit = (data: any) => {
    sendGTMEvent({
      event: gtm.register.try_register
    });
    if (!isLoading) {
      setMutate({
        ...data,
        register_tracker: trackProperty,
        timezone: timeZoneDb
      });
    }
  };
  const handleChangeEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.currentTarget;
    if (isValidEmail(value) && error) {
      setDeleteError(name);
    }
  };
  const handleChangePassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name
    } = e.currentTarget;
    if (hooksForm.watch().password?.length > 8 && error) {
      setDeleteError(name);
    }
  };
  useEffect(() => {
    sendGTMEvent({
      event: gtm.register.visited
    });
    if (access_token) {
      router.reload();
    }
    setError(null);
    cookiePersistStorage.setItem("auth_intent", "register");
  }, []);

  // error logs
  useEffect(() => {
    const passwordError = error?.["password"];
    const emailError = error?.["email"];
    if (error) {
      sendGTMEvent({
        event: gtm.register.any_error
      });
    }
    if (passwordError) sendGTMEvent({
      event: gtm.register.password_error
    });
    if (emailError) sendGTMEvent({
      event: gtm.register.email_error
    });
    if (isSuccess) sendGTMEvent({
      event: gtm.register.register_success
    });
  }, [error, isSuccess]);
  return <div className={classes.Wrapper} data-sentry-component="Register" data-sentry-source-file="index.tsx">
      <SeoHead title={t("signupSeo")} isIndex data-sentry-element="SeoHead" data-sentry-source-file="index.tsx" />
      <div className={classes.back} />
      <When condition={isError} data-sentry-element="When" data-sentry-source-file="index.tsx">
        <Alert icon={<LuAlertTriangle size={29} />} variant="danger" style={{
        margin: "0 auto 25px",
        maxWidth: "544px"
      }} data-sentry-element="Alert" data-sentry-source-file="index.tsx">
          <p className={classes.alertText}>
            <strong>{t("loginAlertText")}</strong>
          </p>
        </Alert>
      </When>
      <div className={classes.LogoWrapper}>
        <div className={classes.Logo}>
          <Link href={"/"} data-sentry-element="Link" data-sentry-source-file="index.tsx">
            <Image alt={logoAltImg} src={"/common/dashboard-logo.svg"} width={335} height={38} data-sentry-element="Image" data-sentry-source-file="index.tsx" />
          </Link>
        </div>
      </div>
      <div className={classes.Content}>
        <div>
          <div className={classes.Title}>{t("getStarted")}</div>
          <form onSubmit={hooksForm.handleSubmit(onSubmit)}>
            <div className={classes.InputBox}>
              <span className={classes.InputTitle}>{t("emailAddress")}</span>
              <input {...hooksForm.register("email", {
              onChange: handleChangeEmail
            })} type={"text"} className={cn(classes.Input, returnErrorAsString("email", "formInput"))} placeholder={t("exampleEmail")} autoComplete="email" defaultValue={typedEmail !== "" ? typedEmail : ""} />
              <Validation type="email" icon data-sentry-element="Validation" data-sentry-source-file="index.tsx" />
            </div>
            <div className={classes.InputBox}>
              <span className={classes.InputTitle}>{t("password")}</span>
              <input {...hooksForm.register("password", {
              onChange: handleChangePassword
            })} autoComplete="password" type={!isConfirmEye ? "password" : "text"} className={cn(classes.Input, classes.PasswordInput, returnErrorAsString("password", "formInput"))} placeholder={t("passwordHint")} />
              <span className={classes.Eye} onClick={() => setIsConfirmEye(!isConfirmEye)}>
                {!isConfirmEye ? <AiOutlineEyeInvisible color="#A1A1A1" size={22} /> : <AiOutlineEye color="#A1A1A1" size={22} />}
              </span>
              <Validation type="password" icon data-sentry-element="Validation" data-sentry-source-file="index.tsx" />
            </div>
            <button className={classes.SubmitButton} onClick={handleRegisterTrack} disabled={isLoading}>
              {isLoading ? <BiLoaderAlt color={"#ffffff"} size={20} /> : t("signUp")}
            </button>
            <OrLine text={t("or")} data-sentry-element="OrLine" data-sentry-source-file="index.tsx" />
            <div className={classes.SocialBox}>
              <GoogleButton text={t("signUpWithGoogle")} onClick={e => {
              sendGTMEvent({
                event: gtm.register.google_register_try
              });
              onSignInGoogle(e);
            }} loading={isGoogleLoading} data-sentry-element="GoogleButton" data-sentry-source-file="index.tsx" />
              <AzureButton onClick={e => {
              sendGTMEvent({
                event: gtm.register.azure_register_try
              });
              onSignInAzure(e);
            }} text={t("signUpWithMicrosoft")} data-sentry-element="AzureButton" data-sentry-source-file="index.tsx" />
            </div>
            <div className={classes.Login}>
              {t("alreadyHaveAccount")}
              {" - "}
              <Link href={"/login"} data-sentry-element="Link" data-sentry-source-file="index.tsx">{t("logIn")}</Link>
            </div>
          </form>
          <p className={classes.FreeTrialBox}>{t("tryServicesFree")}</p>
          <p className={classes.AgreeBox}>
            {t.rich("bySigningUpYouAgree", {
            terms: children => <Link href={"/terms"}>{children}</Link>,
            privacy: children => <Link href={"/policy"}>{children}</Link>
          })}
          </p>
        </div>
      </div>
    </div>;
};
export const getStaticProps: GetStaticProps = async ({
  locale
}) => {
  return {
    props: {
      messages: (await import(`@/messages/qrcodecreator/${locale || "en"}/authentication.json`)).default
    }
  };
};
export default Register;